import type { ReactElement } from 'react';
import { Grid, Text, useScreenSize } from '@stitch-fix/mode-react';
import { A, F } from '@mobily/ts-belt';
import { BoughtItemsBoughtItemFragment } from '../../utils/hooks/getBoughtItems';
import BoughtItemsProductCard from './BoughtItemsProductCard';
import { BoughtItemsOutfits } from './BoughtItemsOutfits';
import { useWardrobeCopy } from '../../utils/contexts/copy-provider';
interface BoughtItemsProps {
  boughtItems: BoughtItemsBoughtItemFragment[];
}
interface PartitionElementsForPageParams {
  boughtItems: BoughtItemsBoughtItemFragment[];
  splitIndex: number;
}

/**
 * Handles splitting the products into the first and the rest of the rows, based on the splitIndex.
 * It inserts the outfits carousel into the rest of the rows, when page is 1.
 */
const partitionElementsForPage = ({
  boughtItems,
  splitIndex
}: PartitionElementsForPageParams): [ReactElement[], ReactElement[]] => {
  const productCards = boughtItems.filter(({
    variant
  }) => !variant.isHidden).map(item => <BoughtItemsProductCard key={item.id} boughtItem={item} />);
  // splitAt returns undefined if splitIndex is out of bounds,
  // so we default to an empty array for the remaining product cards.
  const [firstRow, remainingProductCards] = A.splitAt(productCards, splitIndex) || [productCards, []];
  return [F.toMutable(firstRow), F.toMutable(remainingProductCards)];
};

/**
 * The `BoughtItems` Component renders the grid and product cards for the Bought Page
 */
const BoughtItems = ({
  boughtItems
}: BoughtItemsProps) => {
  const {
    copy
  } = useWardrobeCopy();
  const screenSize = useScreenSize();
  const splitIndex = screenSize === 'md' ? 4 : 6;
  const [firstRow, remainingElements] = partitionElementsForPage({
    boughtItems,
    splitIndex
  });
  return <>
      <Text mb={1} setting="title-medium" align="left">
        {copy.boughtItemsModuleTitle}
      </Text>
      {A.isNotEmpty(firstRow) && <Grid columns={{
      sm: 1,
      md: 2,
      lg: 3
    }} spacing={{
      sm: 'wide'
    }} mb={2}>
          {firstRow}
        </Grid>}

      <BoughtItemsOutfits />

      {A.isNotEmpty(remainingElements) && <Grid columns={{
      sm: 1,
      md: 2,
      lg: 3
    }} spacing={{
      sm: 'wide'
    }} mb={2}>
          {remainingElements}
        </Grid>}
    </>;
};
export default BoughtItems;